import React, { useState, useCallback, useEffect, useRef } from "react"
import { PhoneSlash, ArrowsLeftRight, Spinner, X } from "@phosphor-icons/react"
import { toast } from "sonner"
import { cn } from "../../../lib/utils"
import { useCallStore } from "../../../stores"
import { api } from "../../../lib/api/client"
import { AgentAvatar } from "../AgentAvatar"

const POLL_DELAY_MS = 5000
const POLL_INTERVAL_MS = 3000

interface TransferStatusPanelProps {
  collapsed?: boolean
}

export function TransferStatusPanel({ collapsed }: TransferStatusPanelProps) {
  const { currentCall, transferCall, setTransferCall } = useCallStore()
  const [isMerging, setIsMerging] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const delayRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Polling fallback: if the socket event never arrives, poll the server
  // to check if the transfer call was actually answered
  useEffect(() => {
    const callSid = currentCall?.call_sid
    if (!transferCall || transferCall.can_merge || !callSid) {
      if (pollRef.current) clearInterval(pollRef.current)
      if (delayRef.current) clearTimeout(delayRef.current)
      pollRef.current = null
      delayRef.current = null
      return
    }

    delayRef.current = setTimeout(() => {
      pollRef.current = setInterval(async () => {
        try {
          const res = await api.callCenter.checkTransferStatus(callSid)
          if (res.success && res.data?.answered) {
            const current = useCallStore.getState().transferCall
            if (current && !current.can_merge) {
              useCallStore.getState().setTransferCall({ ...current, can_merge: true })
            }
          }
        } catch {
          // Silently ignore — socket path may still deliver
        }
      }, POLL_INTERVAL_MS)
    }, POLL_DELAY_MS)

    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (delayRef.current) clearTimeout(delayRef.current)
      pollRef.current = null
      delayRef.current = null
    }
  }, [transferCall, currentCall?.call_sid])

  const handleCancelTransfer = useCallback(async () => {
    if (!currentCall?.call_sid) return

    setIsCancelling(true)
    try {
      await api.callCenter.cancelTransfer({ callSid: currentCall.call_sid })
      setTransferCall(null)
    } catch (error) {
      console.error("Cancel transfer error:", error)
      toast.error("Failed to cancel transfer")
    } finally {
      setIsCancelling(false)
    }
  }, [currentCall, setTransferCall])

  const handleMergeCalls = useCallback(async () => {
    if (!currentCall?.call_sid || !transferCall?._id) return

    setIsMerging(true)
    try {
      const response = await api.callCenter.mergeCall({
        callSid: currentCall.call_sid,
        transferId: transferCall._id,
        type: transferCall.type,
        user: transferCall.type === 'user' ? transferCall._id : undefined,
        contact: transferCall.type === 'contact' ? transferCall._id : undefined,
        number: transferCall.type === 'number' ? transferCall._id : undefined,
      })

      if (response.success) {
        setTransferCall(null)
        toast.success("Calls merged successfully")
      } else {
        toast.error("Failed to merge calls")
      }
    } catch (error) {
      console.error("Merge calls error:", error)
      toast.error("Failed to merge calls")
    } finally {
      setIsMerging(false)
    }
  }, [currentCall, transferCall, setTransferCall])

  if (!transferCall) return null

  // Get initials from transfer name
  const getInitials = () => {
    if (!transferCall.display_name) return "?"
    const parts = transferCall.display_name.split(" ")
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return transferCall.display_name.slice(0, 2).toUpperCase()
  }

  if (collapsed) {
    return (
      <div className="py-2 bg-info/20 border-t border-info/30">
        <div className="flex flex-col items-center gap-1">
          <AgentAvatar initials={getInitials()} status="ringing" size="sm" />
          {!transferCall.can_merge && (
            <Spinner className="h-3 w-3 animate-spin text-info" />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-b from-info/20 to-info/10 border-t border-info/30">
      <div className="px-4 py-4">
        {/* Transfer recipient info */}
        <div className="flex items-center gap-3">
          <AgentAvatar initials={getInitials()} status="ringing" size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {transferCall.display_name || "Unknown"}
            </p>
            <p className="text-xs text-muted-foreground">
              {transferCall.type === "user" ? "User" : transferCall.type === "contact" ? "Contact" : "External"}
            </p>
          </div>
          <button
            onClick={handleCancelTransfer}
            disabled={isCancelling}
            className={cn(
              "p-1.5 rounded-full transition-colors",
              "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
              isCancelling && "opacity-50 cursor-not-allowed"
            )}
            title="Cancel Transfer"
          >
            {isCancelling ? (
              <Spinner className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Status and action buttons */}
        <div className="mt-3 space-y-2">
          {transferCall.can_merge ? (
            <button
              onClick={handleMergeCalls}
              disabled={isMerging}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2 px-3 rounded-md",
                "bg-success text-white font-medium text-sm",
                "hover:bg-success/90 transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isMerging ? (
                <>
                  <Spinner className="h-4 w-4 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <ArrowsLeftRight className="h-4 w-4" />
                  Merge Calls
                </>
              )}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 py-2 px-3 rounded-md bg-muted/50">
              <Spinner className="h-4 w-4 animate-spin text-info" />
              <span className="text-sm text-muted-foreground">Ringing...</span>
            </div>
          )}

          <button
            onClick={handleCancelTransfer}
            disabled={isCancelling}
            className={cn(
              "w-full flex items-center justify-center gap-2 py-2 px-3 rounded-md",
              "border border-destructive/30 text-destructive font-medium text-sm",
              "hover:bg-destructive/10 transition-colors",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isCancelling ? (
              <>
                <Spinner className="h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              <>
                <PhoneSlash className="h-4 w-4" />
                Cancel Transfer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
